import { useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import Input from "../../../../../common/components/input-service-request/Input";
import { Button } from "../../../../customer/components";
import Select from "../../../../../common/components/input-service-request/Select";
import PhoneInput from "../../../../../common/ekedc-components/phone-number-input/PhoneInput";
import { validatePhoneNumber } from "../../../../customer/validators";
import { useServiceRequestForm } from "../../context/ServiceRequestFormContext";
import { StepProps } from "../../informations";
import styles from "./steps.module.scss";
import { useLazyVerifyPrepaidMeterQuery, useLazyVerifyPostpaidMeterQuery } from "../../../../../service/vending-service";
import { getCurrentUtilityId } from "../../../../../guards/common/authentification-functions";

const METER_TYPE_OPTIONS = [
    { value: "prepaid", label: "Prepaid" },
    { value: "postpaid", label: "Postpaid" },
];

const CustomerDetailsStep = ({ title, message }: StepProps) => {
    const { form: { register, control, trigger, setValue, getValues, formState: { errors, touchedFields } } } = useServiceRequestForm();
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState<string | null>(null);
    const [verifiedInfo, setVerifiedInfo] = useState<{ accountName?: string; address?: string } | null>(null);

    const [triggerPrepaid] = useLazyVerifyPrepaidMeterQuery();
    const [triggerPostpaid] = useLazyVerifyPostpaidMeterQuery();

    const meterNumber = useWatch({ control, name: "meterNumber" });
    const meterType = useWatch({ control, name: "meterType" });
    const meterVerified = useWatch({ control, name: "meterVerified" });

    const canVerify = !!meterNumber?.trim() && !!meterType && !verifying && !meterVerified;

    const resetVerification = () => {
        setValue("meterVerified", false);
        setVerifyError(null);
        setVerifiedInfo(null);
    };

    const handleVerify = async () => {
        const utilityId = getCurrentUtilityId() ?? "";
        const meterNum = getValues("meterNumber").trim();
        const type = getValues("meterType");

        setVerifying(true);
        setVerifyError(null);

        try {
            const result = type === "postpaid"
                ? await triggerPostpaid({ MeterNumber: meterNum, UtilityId: utilityId }).unwrap()
                : await triggerPrepaid({ MeterNumber: meterNum, UtilityId: utilityId }).unwrap();
            setVerifiedInfo({ accountName: result.accountName, address: result.address });
            setValue("meterVerified", true, { shouldValidate: true });
        } catch (err: any) {
            setVerifyError("Meter number not found. Please check and try again.");
            setVerifiedInfo(null);
            setValue("meterVerified", false, { shouldValidate: true });
        } finally {
            setVerifying(false);
        }
    };


    return (
        <div className={styles.mainStepContent}>
            <div className={styles.headerContainer}>
                <h2 className={styles.headerTitle}>{title}</h2>
                <p className={styles.headerDescription}>{message}</p>
            </div>

            <div className={styles.formContainer}>
                <Input
                    label="First Name"
                    placeholder="eg. John"
                    error={errors.firstName?.message}
                    {...register("firstName", { required: "First name is required" })}
                />
                <Input
                    label="Last Name"
                    placeholder="eg. Doe"
                    error={errors.lastName?.message}
                    {...register("lastName", { required: "Last name is required" })}
                />
            </div>
            <hr className={styles.divider} />
            <div className={styles.formContainer}>
                <Input
                    label="Email"
                    placeholder="eg. sample@gmail.com"
                    error={errors.email?.message}
                    {...register("email", {
                        required: "Email is required",
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid email address" },
                    })}
                />
                <Controller
                    name="phone"
                    control={control}
                    rules={{
                        validate: (value) => {
                            if (!value) return "Phone number is required";
                            return validatePhoneNumber(value);
                        },
                    }}
                    render={({ field }) => (
                        <PhoneInput
                            label="Phone Number"
                            placeholder="eg. 08101545673"
                            required
                            value={field.value}
                            onChange={(val) => { field.onChange(val); if (touchedFields.phone) trigger("phone"); }}
                            onBlur={() => { field.onBlur(); trigger("phone"); }}
                            error={errors.phone?.message}
                        />
                    )}
                />
            </div>
            <hr className={styles.divider} />
            <div className={styles.formContainer}>
                <Input
                    label="Home Address"
                    placeholder="eg. 12a Talabi Street, Ikeja"
                    error={errors.homeAddress?.message}
                    {...register("homeAddress", { required: "Home address is required" })}
                />
            </div>
            <hr className={styles.divider} />
            <div className={styles.formContainer}>
                <Controller
                    name="meterType"
                    control={control}
                    rules={{ required: "Meter type is required" }}
                    render={({ field }) => (
                        <Select
                            label="Meter Type"
                            options={METER_TYPE_OPTIONS}
                            value={field.value}
                            error={errors.meterType?.message}
                            onChange={(opt) => {
                                field.onChange(opt.value);
                                resetVerification();
                            }}
                        />
                    )}
                />
            </div>
            <hr className={styles.divider} />
            <div className={styles.formContainer}>
                <div className={styles.meterRow}>
                    <label className={styles.meterLabel}>Meter Number</label>
                    <div className={styles.meterRightGroup}>
                        <Input
                            label=""
                            inputSize="full"
                            placeholder="eg. 4567345353"
                            readOnly={!!meterVerified}
                            error={errors.meterNumber?.message ?? verifyError ?? errors.meterVerified?.message}
                            {...register("meterNumber", {
                                required: "Meter number is required",
                                onChange: () => resetVerification(),
                            })}
                        />
                        <Button
                            type="button"
                            className={`${styles.verifyBtnStyle} ${meterVerified ? styles.verifyBtnStyleSuccess : ""}`}
                            disabled={!canVerify}
                            onClick={handleVerify}
                        >
                            {verifying ? "Verifying" : meterVerified ? "Verified ✓" : "Verify"}
                        </Button>
                    </div>
                </div>
                {meterVerified && verifiedInfo && (
                    <div className={styles.meterVerifiedRow}>
                        <div className={styles.meterVerifiedInfo}>
                            <span className={styles.meterVerifiedName}>{verifiedInfo.accountName || "Account Name unknown"}</span>
                            {verifiedInfo.address && (
                                <span className={styles.meterVerifiedAddress}>{verifiedInfo.address}</span>
                            )}
                        </div>
                    </div>
                )}
                <Controller
                    name="meterVerified"
                    control={control}
                    rules={{ validate: (v) => v === true || "Please verify your meter number before continuing" }}
                    render={() => <></>}
                />
            </div>
        </div>
    );
};

export default CustomerDetailsStep;
